import React, { useMemo, useState, useEffect } from "react";
import { motion } from "motion/react";
import {
  User,
  Mail,
  Phone,
  Shield,
  Calendar,
  Clock,
  Edit,
  CheckCircle2,
  XCircle,
  Copy,
  Gift,
  Sparkles,
  Globe,
  DollarSign,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { useDashboardContext } from "../../../context/DashboardContext";
import {
  TextInput,
  Select,
  Stack,
  Button as MantineButton,
  Modal,
  PasswordInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useToastNotification } from "../../../hooks/useToast";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { HttpCodes } from "../../../lib/data";
import { Currencies } from "../../../lib/data/currencies";
import { Countries } from "../../../lib/data/countries";
import "./styles.scss";

// Transform countries from data file to Mantine Select format
const countries = Countries.map((country) => ({
  value: country.name,
  label: country.name,
}));

// Transform currencies from data file to Mantine Select format
const currencies = Currencies.map((currency) => ({
  value: currency.code,
  label: `${currency.code} - ${currency.name}`,
}));

// Time zones list
const timeZones = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "UTC+1", label: "UTC+1 (Central European Time)" },
  { value: "UTC+2", label: "UTC+2 (Eastern European Time)" },
  { value: "UTC+3", label: "UTC+3 (Moscow Time)" },
  { value: "UTC+4", label: "UTC+4 (Gulf Standard Time)" },
  { value: "UTC+5", label: "UTC+5 (Pakistan Standard Time)" },
  { value: "UTC+5:30", label: "UTC+5:30 (India Standard Time)" },
  { value: "UTC+6", label: "UTC+6 (Bangladesh Standard Time)" },
  { value: "UTC+7", label: "UTC+7 (Indochina Time)" },
  { value: "UTC+8", label: "UTC+8 (China Standard Time)" },
  { value: "UTC+9", label: "UTC+9 (Japan Standard Time)" },
  { value: "UTC+10", label: "UTC+10 (Australian Eastern Time)" },
  { value: "UTC+11", label: "UTC+11 (Solomon Islands Time)" },
  { value: "UTC+12", label: "UTC+12 (New Zealand Time)" },
  { value: "UTC-1", label: "UTC-1 (Cape Verde Time)" },
  { value: "UTC-2", label: "UTC-2 (South Georgia Time)" },
  { value: "UTC-3", label: "UTC-3 (Argentina Time)" },
  { value: "UTC-4", label: "UTC-4 (Atlantic Standard Time)" },
  { value: "UTC-5", label: "UTC-5 (Eastern Standard Time)" },
  { value: "UTC-6", label: "UTC-6 (Central Standard Time)" },
  { value: "UTC-7", label: "UTC-7 (Mountain Standard Time)" },
  { value: "UTC-8", label: "UTC-8 (Pacific Standard Time)" },
  { value: "UTC-9", label: "UTC-9 (Alaska Standard Time)" },
  { value: "UTC-10", label: "UTC-10 (Hawaii-Aleutian Time)" },
  { value: "UTC-11", label: "UTC-11 (Samoa Standard Time)" },
  { value: "UTC-12", label: "UTC-12 (Baker Island Time)" },
];

const Profile: React.FC = () => {
  const { profile, isLoading, reloadProfile } = useDashboardContext();
  const toast = useToastNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const onboardingForm = useForm({
    initialValues: {
      phone_number: "",
      country: "",
      currency: "",
      time_zone: "",
    },
    validate: {
      phone_number: (value: string) => (value.trim().length > 0 ? null : "Phone number is required"),
      country: (value: string) => (value ? null : "Country is required"),
      currency: (value: string) => (value ? null : "Currency is required"),
      time_zone: (value: string) => (value.trim().length > 0 ? null : "Time zone is required"),
    },
  });

  const passwordForm = useForm({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: {
      password: (value: string) =>
        value.length >= 8 ? null : "Password must be at least 8 characters",
      confirmPassword: (value: string, values) =>
        value === values.password ? null : "Passwords do not match",
    },
  });

  // Update form when profile loads
  useEffect(() => {
    if (profile) {
      onboardingForm.setFieldValue("phone_number", profile.phone_number || "");
    }
  }, [profile]);

  const initials = useMemo(() => {
    if (!profile) return "AW";
    return (
      (profile.first_name?.charAt(0) ?? "") +
      (profile.last_name?.charAt(0) ?? "")
    ).toUpperCase() || "AW";
  }, [profile]);

  const fullName = useMemo(() => {
    if (!profile) return "User";
    return `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim() || "User";
  }, [profile]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "Invalid date";
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Referral code copied to clipboard.");
  };

  const handleOnboardingSubmit = async (values: typeof onboardingForm.values) => {
    setIsSubmitting(true);
    toast.info("Setting up your onboarding details...");

    const response = await PerformRequest({
      method: Endpoints.GetSetOnboardingDetails().method,
      route: Endpoints.GetSetOnboardingDetails().route,
      data: {
        phone_number: values.phone_number.trim(),
        country: values.country,
        currency: values.currency,
        time_zone: values.time_zone.trim(),
      },
      onError: () => {
        setIsSubmitting(false);
      },
    });

    setIsSubmitting(false);

    if (response.status === HttpCodes.Created) {
      toast.success("Onboarding details saved successfully!");
      onboardingForm.reset();
      reloadProfile();
    } else {
      toast.error(response.message ?? "Failed to save onboarding details. Please try again.");
    }
  };

  const handlePasswordSubmit = async (values: typeof passwordForm.values) => {
    if (!profile) return;
    setIsUpdatingPassword(true);
    const response = await PerformRequest({
      method: Endpoints.GetUpdatePassword().method,
      route: Endpoints.GetUpdatePassword().route,
      data: {
        password: values.password,
        email: profile.email,
      },
      onError: () => setIsUpdatingPassword(false),
    });

    setIsUpdatingPassword(false);

    if (response.status === HttpCodes.Success || response.status === HttpCodes.Created) {
      toast.success("Password updated successfully.");
      passwordForm.reset();
      setPasswordModalOpen(false);
    } else {
      toast.error(response.message ?? "Unable to update password. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="loading-spinner"
          />
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="profile-container">
        <div className="profile-error">
          <p>Unable to load profile. Please try again.</p>
          <Button onClick={reloadProfile}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="profile-hero"
      >
        <div className="profile-hero__background" />
        <div className="profile-hero__content">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="profile-hero__avatar-wrapper"
          >
            <Avatar className="profile-hero__avatar">
              <AvatarImage src={profile.avatar_url || undefined} alt={fullName} />
              <AvatarFallback className="profile-hero__avatar-fallback">
                {initials}
              </AvatarFallback>
            </Avatar>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="profile-hero__edit-badge"
            >
              <Edit className="h-3.5 w-3.5" />
            </motion.div>
          </motion.div>

          <div className="profile-hero__info">
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="profile-hero__name"
            >
              {fullName}
              {profile.is_active && (
                <motion.span
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5, type: "spring" }}
                  className="profile-hero__status-badge"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Active</span>
                </motion.span>
              )}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="profile-hero__email"
            >
              {profile.email}
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="profile-hero__actions"
          >
            <Button
              variant="outline"
              className="profile-hero__edit-button"
              onClick={() => {
                // Add edit functionality here
              }}
            >
              <Edit className="h-4 w-4" />
              <span>Edit Profile</span>
            </Button>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Content Grid */}
      <div className="profile-content-grid">
        {/* Personal Information Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="profile-card-wrapper"
        >
          <Card className="profile-card profile-card--personal">
            <CardHeader>
              <CardTitle className="profile-card__title">
                <User className="h-5 w-5" />
                Personal Information
              </CardTitle>
              <CardDescription>
                Your basic profile details and contact information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="profile-info-grid">
                <div className="profile-info-item">
                  <div className="profile-info-item__label">
                    <User className="h-4 w-4" />
                    <span>FULL NAME</span>
                  </div>
                  <div className="profile-info-item__value">{fullName}</div>
                </div>

                <div className="profile-info-item">
                  <div className="profile-info-item__label">
                    <Mail className="h-4 w-4" />
                    <span>EMAIL ADDRESS</span>
                  </div>
                  <div className="profile-info-item__value-group">
                    <span className="profile-info-item__value">{profile.email}</span>
                    <Badge
                      variant={profile.email_verified ? "default" : "secondary"}
                      className="profile-info-item__badge"
                      data-variant={profile.email_verified ? "default" : "secondary"}
                    >
                      {profile.email_verified ? (
                        <>
                          <CheckCircle2 className="h-3 w-3" />
                          <span>Verified</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3" />
                          <span>Unverified</span>
                        </>
                      )}
                    </Badge>
                  </div>
                </div>

                <div className="profile-info-item">
                  <div className="profile-info-item__label">
                    <Phone className="h-4 w-4" />
                    <span>PHONE NUMBER</span>
                  </div>
                  <div className="profile-info-item__value-group">
                    <span className="profile-info-item__value">
                      {profile.phone_number || "Not provided"}
                    </span>
                    {profile.phone_number && (
                      <Badge
                        variant={profile.phone_verified ? "default" : "secondary"}
                        className="profile-info-item__badge"
                        data-variant={profile.phone_verified ? "default" : "secondary"}
                      >
                        {profile.phone_verified ? (
                          <>
                            <CheckCircle2 className="h-3 w-3" />
                            <span>Verified</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3 w-3" />
                            <span>Unverified</span>
                          </>
                        )}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Account Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="profile-card-wrapper"
        >
          <Card className="profile-card profile-card--account">
            <CardHeader>
              <CardTitle className="profile-card__title">
                <Shield className="h-5 w-5" />
                Account Details
              </CardTitle>
              <CardDescription>
                Your account status and security information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="profile-info-grid">
                <div className="profile-info-item">
                  <div className="profile-info-item__label">
                    <Shield className="h-4 w-4" />
                    <span>ACCOUNT STATUS</span>
                  </div>
                  <div className="profile-info-item__status-input">
                    <input
                      type="text"
                      value={profile.is_active ? "Active" : "Inactive"}
                      readOnly
                      className="profile-status-input"
                    />
                  </div>
                </div>

                <div className="profile-info-item">
                  <div className="profile-info-item__label">
                    <Calendar className="h-4 w-4" />
                    <span>DATE JOINED</span>
                  </div>
                  <div className="profile-info-item__value">
                    {formatDate(profile.date_joined)}
                  </div>
                </div>

                <div className="profile-info-item">
                  <div className="profile-info-item__label">
                    <Clock className="h-4 w-4" />
                    <span>LAST LOGIN</span>
                  </div>
                  <div className="profile-info-item__value">
                    {formatDate(profile.last_login)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Password Update Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.5 }}
          className="profile-card-wrapper"
        >
          <Card className="profile-card profile-card--password">
            <CardHeader>
              <CardTitle className="profile-card__title">
                <Shield className="h-5 w-5" />
                Password & Security
              </CardTitle>
              <CardDescription>
                Keep your workspace locked down with a fresh password.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="profile-password-section">
                <p className="profile-password-hint">
                  We recommend updating your password every couple months to keep
                  your account secure.
                </p>
                <MantineButton
                  size="md"
                  radius="sm"
                  onClick={() => setPasswordModalOpen(true)}
                >
                  Update Password
                </MantineButton>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Onboarding Details Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="profile-card-wrapper profile-card-wrapper--full"
        >
          <Card className="profile-card profile-card--onboarding">
            <CardHeader>
              <CardTitle className="profile-card__title">
                <Globe className="h-5 w-5" />
                Onboarding Details
              </CardTitle>
              <CardDescription>
                Complete your profile by setting up your location and preferences
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onboardingForm.onSubmit(handleOnboardingSubmit)}>
                <Stack gap="md">
                  <TextInput
                    label="Phone Number"
                    placeholder="09092202826"
                    size="md"
                    radius="sm"
                    leftSection={<Phone className="h-4 w-4" />}
                    {...onboardingForm.getInputProps("phone_number")}
                  />

                  <Select
                    label="Country"
                    placeholder="Select your country"
                    size="md"
                    radius="sm"
                    data={countries}
                    leftSection={<Globe className="h-4 w-4" />}
                    searchable
                    {...onboardingForm.getInputProps("country")}
                  />

                  <Select
                    label="Currency"
                    placeholder="Select your currency"
                    size="md"
                    radius="sm"
                    data={currencies}
                    leftSection={<DollarSign className="h-4 w-4" />}
                    searchable
                    {...onboardingForm.getInputProps("currency")}
                  />

                  <Select
                    label="Time Zone"
                    placeholder="Select your time zone"
                    size="md"
                    radius="sm"
                    data={timeZones}
                    leftSection={<Clock className="h-4 w-4" />}
                    searchable
                    {...onboardingForm.getInputProps("time_zone")}
                  />

                  <MantineButton
                    type="submit"
                    size="lg"
                    radius="sm"
                    className="profile-onboarding__submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Save Onboarding Details
                  </MantineButton>
                </Stack>
              </form>
            </CardContent>
          </Card>
        </motion.div>

        {/* Referral Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="profile-card-wrapper profile-card-wrapper--full"
        >
          <Card className="profile-card profile-card--referral">
            <CardHeader>
              <CardTitle className="profile-card__title">
                <Gift className="h-5 w-5" />
                Referral Code
              </CardTitle>
              <CardDescription>
                Share your referral code with friends and earn rewards
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="profile-referral-section">
                <div className="profile-referral-code">
                  <code className="profile-referral-code__text">
                    {profile.referral_code}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    className="profile-referral-code__copy"
                    onClick={() => copyToClipboard(profile.referral_code)}
                  >
                    <Copy className="h-4 w-4" />
                    <span>Copy</span>
                  </Button>
                </div>
                <p className="profile-referral-hint">
                  Share this code with others to invite them to join
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
      <Modal
        opened={passwordModalOpen}
        onClose={() => {
          setPasswordModalOpen(false);
          passwordForm.reset();
        }}
        centered
        title="Update password"
        overlayProps={{ backgroundOpacity: 0.4, blur: 2 }}
      >
        <form onSubmit={passwordForm.onSubmit(handlePasswordSubmit)}>
          <Stack gap="md">
            <PasswordInput
              label="New password"
              placeholder="Enter a strong password"
              radius="sm"
              size="md"
              {...passwordForm.getInputProps("password")}
            />
            <PasswordInput
              label="Confirm password"
              placeholder="Re-enter new password"
              radius="sm"
              size="md"
              {...passwordForm.getInputProps("confirmPassword")}
            />
            <MantineButton
              type="submit"
              size="md"
              radius="sm"
              loading={isUpdatingPassword}
              disabled={isUpdatingPassword}
            >
              Save Password
            </MantineButton>
          </Stack>
        </form>
      </Modal>
    </div>
  );
};

export default Profile;


